import React from "react";
import { getLocaleId } from "shared/utils";
import { TicketCardFlowType } from "../../enum";
import { useCopies } from "shared/hooks/useCopies";
import { useSearchParams } from "react-router-dom";
import InventoryType from "shared/enums/InventoryType";
import QueryParamKey from "shared/enums/QueryParamKey";
import { isBuyerAttendee } from "apps/registration/utils";
import { TicketFooterPropTypes, TicketFooterService } from "./types";
import RegistrationWorkflowType from "shared/enums/RegistrationWorkflowType";

export default function useTicketFooterService(props: TicketFooterPropTypes): TicketFooterService {
  const copies = useCopies();
  const [searchParams] = useSearchParams();
  const localeId = React.useMemo(() => props.localeId || getLocaleId(), [props.localeId]);

  // for Upgrade ticket
  const hasPriceDifference = React.useMemo(() => {
    return (
      props.ticketCardFlowType === TicketCardFlowType.UPGRADE_TICKET &&
      props.currentPrice?.value !== props.priceDifference?.value
    );
  }, [props.ticketCardFlowType, props.currentPrice?.value, props.priceDifference?.value]);

  const buyerAttendee = React.useMemo(() => {
    return props.subRole && isBuyerAttendee(props.subRole);
  }, [props]);

  const buyTicketButtonLabel = React.useMemo(
    () =>
      props.ticketCardFlowType === TicketCardFlowType.REG_BULK_TICKET
        ? copies.get("proceed")
        : props.paymentFlow || props.workflowType === RegistrationWorkflowType.NON_ATTENDEE_NO_APPROVAL
        ? copies.get("get.ticket")
        : copies.get("apply.now"),
    [copies, props.paymentFlow, props.ticketCardFlowType, props.workflowType]
  );

  const getTicketClickHandler = React.useCallback(
    (hasUrl?: boolean) => {
      let ticketUrl = props.ticketUrl;
      if (searchParams.get(QueryParamKey.PREVIEW_MODE)) ticketUrl += `&${QueryParamKey.PREVIEW_MODE}=true`;

      if (hasUrl) {
        window.open(ticketUrl, "_blank", "noopener,noreferrer");
      } else {
        props.buyTicketClickHandler?.(props.id);
      }
    },
    [searchParams, props]
  );

  const showTicketPurchaseButtons = React.useMemo(() => {
    let ticketButton =
      props.ticketCardFlowType === TicketCardFlowType.REG_GET_TICKET ||
      props.ticketCardFlowType === TicketCardFlowType.REG_BULK_TICKET;
    let bulkTicketButton = Boolean(props.bulkPurchaseAllowed && props.buyTicketClickHandler);
    // todo:TW temporary fix: added props.workflowType === RegistrationWorkflowType.APPLICATION
    if (props.showSingleBuyButtonAtOnce) {
      ticketButton =
        ticketButton &&
        (props.applicableRate?.inventoryType === InventoryType.TICKET ||
          props.workflowType === RegistrationWorkflowType.APPLICATION);
      bulkTicketButton = bulkTicketButton && props.applicableRate?.inventoryType === InventoryType.BULK_TICKET;
    }
    return { ticketButton, bulkTicketButton };
  }, [
    props.applicableRate?.inventoryType,
    props.bulkPurchaseAllowed,
    props.buyTicketClickHandler,
    props.showSingleBuyButtonAtOnce,
    props.ticketCardFlowType,
    props.workflowType
  ]);

  return {
    buyerAttendee,
    hasPriceDifference,
    localeId,
    buyTicketButtonLabel,
    getTicketClickHandler,
    showTicketPurchaseButtons
  };
}
